// spokeGrowthTimeline
//
// Turns a bag of cached referral records + hub facility ids + a direction
// filter into an ordered event stream the map animation can walk through.
//
// Vocabulary:
//   Hubs   = facilities in the user's picked scope (customer, saved
//            filter, or single facility). Always visible from frame 0.
//   Spokes = *external* counterpart facilities on qualifying referrals.
//            Direction decides which side is the spoke:
//              - inbound  -> sending_facility is the spoke
//              - outbound -> receiving_facility is the spoke
//              - either   -> counterpart of whichever side is a hub
//
// Hub↔hub (internal) referrals — e.g. UAB West → UAB Hospital when both
// are in the customer hub set — still count toward `stats.totalInScope`,
// but are NOT emitted as map events. Otherwise a single-facility scope
// can look larger than its parent customer, because sister facilities
// count as external spokes for the hospital alone.
//
// Each event records the timestamp of one external referral, its
// spoke facility id, and its direction. The animation compresses
// [startMs, endMs] onto the target play duration, so a single sorted
// pass through `events` is enough to render any playhead position.
//
// The `source` field on events / spokes is currently always 've'. When
// the non-VE physician API lands, we'll emit additional events with
// `source: 'non-ve'` and the map layer can pick a different icon set.

import { unwrapDateLike } from './dateUtils';
import {
  DIRECTION_INBOUND,
  DIRECTION_OUTBOUND,
  DIRECTION_EITHER,
} from './referralDirection';

function toCanonicalId(raw) {
  if (raw == null || raw === '') return null;
  const n = parseFloat(String(raw));
  if (!Number.isFinite(n)) return null;
  return Math.round(n).toString();
}

function inHubSet(hubSet, canonicalId) {
  if (!canonicalId) return false;
  return hubSet.has(canonicalId) || hubSet.has(`${canonicalId}.0`);
}

function extractEventTimestampMs(record) {
  const inner = record?.data || record?.Data || {};
  // Prefer submission_date (when the referral was submitted); fall back
  // to created_date; then to the record's own top-level timestamp.
  const src = inner.submission_date || inner.created_date || record?.timestamp;
  const iso = unwrapDateLike(src);
  if (!iso) return null;
  const d = iso instanceof Date ? iso : new Date(iso);
  const t = d.getTime();
  return Number.isFinite(t) ? t : null;
}

/**
 * Build the event stream + hub/spoke geometry for the animation.
 *
 * @param {object}   opts
 * @param {any[]}    opts.records                Cached referral records.
 * @param {Set<string>} opts.hubFacilityIds      Canonical hub VE ids.
 * @param {string}   opts.direction              inbound | outbound | either.
 * @param {Map}      opts.facilityCoordsByVeId   From the SF hierarchy.
 * @param {string}   opts.startDate              'YYYY-MM-DD' inclusive.
 * @param {string}   opts.endDate                'YYYY-MM-DD' inclusive.
 * @returns {{
 *   hubs: any[],
 *   spokes: any[],
 *   hubsMissingCoords: any[],
 *   spokesMissingCoords: any[],
 *   events: {t:number, spokeVeId:string, direction:string, source:string, hubVeId:string|null}[],
 *   startMs: number,
 *   endMs: number,
 *   stats: { totalInScope: number, externalCount: number, internalCount: number },
 * }}
 */
export function buildSpokeGrowthTimeline({
  records,
  hubFacilityIds,
  direction,
  facilityCoordsByVeId,
  startDate,
  endDate,
}) {
  const startMs = new Date(startDate + 'T00:00:00').getTime();
  const endMs = new Date(endDate + 'T23:59:59').getTime();
  const hubSet = hubFacilityIds instanceof Set
    ? hubFacilityIds
    : new Set(Array.from(hubFacilityIds || []).map(String));

  // Materialize hub facility metadata up-front so we can bail cleanly if
  // a hub has no coords rather than silently dropping it from the map.
  const hubs = [];
  const hubsMissingCoords = [];
  const seenHubCanonical = new Set();
  hubSet.forEach(rawId => {
    const canonical = toCanonicalId(rawId);
    if (!canonical || seenHubCanonical.has(canonical)) return;
    seenHubCanonical.add(canonical);
    const f = facilityCoordsByVeId.get(canonical);
    if (!f) {
      hubsMissingCoords.push({
        veId: canonical,
        name: `Facility ${canonical}`,
        source: 've',
      });
      return;
    }
    if (f.lat != null && f.lng != null) hubs.push({ ...f, source: 've' });
    else hubsMissingCoords.push({ ...f, source: 've' });
  });

  // spoke aggregate: veId -> { veId, name, count, first, direction, source }
  // "direction" on the spoke is the direction that first surfaced it in
  // the timeline (so mixed 'either' mode keeps a stable per-marker icon).
  // "first" is the timestamp of its first event so we can sort spokes
  // chronologically without walking the whole event list a second time.
  const spokeAgg = new Map();
  const events = [];
  // totalInScope counts every referral that touches the hub set in the
  // requested direction — including hub↔hub (e.g. UAB West → UAB Hospital
  // when the customer hub set includes both). The map/animation only
  // plots *external* spokes, so without this split a single-facility
  // scope can show MORE "referrals in scope" than its parent customer:
  // sister facilities count as spokes for the hospital alone, but are
  // internal (and previously dropped entirely) for the health system.
  let totalInScope = 0;
  let externalCount = 0;
  let internalCount = 0;

  for (const r of records || []) {
    const t = extractEventTimestampMs(r);
    if (t == null || t < startMs || t > endMs) continue;

    const inner = r?.data || r?.Data || {};
    const sendId = toCanonicalId(inner.sending_facility_id);
    const recvId = toCanonicalId(inner.receiving_facility_id);
    const sendIsHub = inHubSet(hubSet, sendId);
    const recvIsHub = inHubSet(hubSet, recvId);

    // Qualify against the hub set first (counts), then decide whether
    // there is an external spoke worth plotting on the map.
    let hubId = null;
    let spokeId = null;
    let evDir = null;
    let isInternal = false;

    if (direction === DIRECTION_INBOUND) {
      // Into a hub from anyone else (including other hubs in-scope).
      if (recvIsHub && sendId && sendId !== recvId) {
        hubId = recvId;
        evDir = DIRECTION_INBOUND;
        if (sendIsHub) isInternal = true;
        else spokeId = sendId;
      }
    } else if (direction === DIRECTION_OUTBOUND) {
      // Out of a hub to anyone else (including other hubs in-scope).
      if (sendIsHub && recvId && sendId !== recvId) {
        hubId = sendId;
        evDir = DIRECTION_OUTBOUND;
        if (recvIsHub) isInternal = true;
        else spokeId = recvId;
      }
    } else if (direction === DIRECTION_EITHER) {
      // Prefer inbound classification when the receiver is a hub; otherwise
      // outbound. Hub↔hub still counts as in-scope internal.
      if (recvIsHub && sendId && sendId !== recvId) {
        hubId = recvId;
        evDir = DIRECTION_INBOUND;
        if (sendIsHub) isInternal = true;
        else spokeId = sendId;
      } else if (sendIsHub && recvId && sendId !== recvId) {
        hubId = sendId;
        evDir = DIRECTION_OUTBOUND;
        if (recvIsHub) isInternal = true;
        else spokeId = recvId;
      }
    }

    if (!hubId || !evDir) continue;

    totalInScope += 1;
    if (isInternal || !spokeId) {
      internalCount += 1;
      continue; // counted, but no external spoke to animate
    }
    externalCount += 1;

    // Aggregate spoke metadata.
    const facilityLookupName = (() => {
      // Prefer the cleaner facility name from SF; fall back to whatever
      // the referral row carries so odd/legacy facilities still label.
      const cached = facilityCoordsByVeId.get(spokeId);
      if (cached?.name) return cached.name;
      if (evDir === DIRECTION_INBOUND) return inner.sending_facility || `Facility ${spokeId}`;
      return inner.receiving_facility || `Facility ${spokeId}`;
    })();

    let sp = spokeAgg.get(spokeId);
    if (!sp) {
      sp = {
        veId: spokeId,
        name: facilityLookupName,
        direction: evDir,
        source: 've',
        count: 0,
        first: t,
      };
      spokeAgg.set(spokeId, sp);
    } else if (t < sp.first) {
      sp.first = t;
    }
    sp.count += 1;

    events.push({
      t,
      spokeVeId: spokeId,
      hubVeId: hubId,
      direction: evDir,
      source: 've',
    });
  }

  // Sort events by time — the render loop and MP4 exporter both assume
  // ascending timestamps.
  events.sort((a, b) => a.t - b.t);

  // Split aggregated spokes by coord availability.
  const spokes = [];
  const spokesMissingCoords = [];
  spokeAgg.forEach(sp => {
    const f = facilityCoordsByVeId.get(sp.veId);
    if (f && f.lat != null && f.lng != null) {
      spokes.push({
        ...f,
        name: sp.name || f.name,
        veId: sp.veId,
        direction: sp.direction,
        source: sp.source,
        count: sp.count,
        first: sp.first,
      });
    } else {
      spokesMissingCoords.push({
        veId: sp.veId,
        name: sp.name,
        street: f?.street ?? null,
        city: f?.city ?? null,
        state: f?.state ?? null,
        postalCode: f?.postalCode ?? null,
        country: f?.country ?? null,
        direction: sp.direction,
        source: sp.source,
        count: sp.count,
        first: sp.first,
      });
    }
  });

  return {
    hubs,
    spokes,
    hubsMissingCoords,
    spokesMissingCoords,
    events,
    startMs,
    endMs,
    stats: {
      totalInScope,
      externalCount,
      internalCount,
    },
  };
}

/**
 * Given an event stream and a playhead ∈ [0,1], return the spokes that
 * should be visible at that point plus their cumulative counts.
 *
 * Playhead maps linearly to wall time between startMs and endMs (the
 * timeline is already sorted). This is O(N) per call, which is fine for
 * a few thousand events per animation and lets us avoid maintaining a
 * separate mutable state machine across React renders.
 */
export function advanceStateToTime({ events, playhead, startMs, endMs }) {
  const p = Math.max(0, Math.min(1, Number(playhead) || 0));
  const t = startMs + (endMs - startMs) * p;

  // First pass finds the last event index whose t <= our current time.
  // Because events is sorted, a linear scan is fine; if this ever gets
  // slow for the largest customers, swap in a binary search.
  let lastIdx = -1;
  for (let i = 0; i < events.length; i++) {
    if (events[i].t <= t) lastIdx = i;
    else break;
  }

  // Second pass aggregates counts + first/last-seen time for each spoke.
  // firstT drives the "lines fly in" effect; lastT drives the brief
  // "pop" flash when a new referral hits an existing spoke.
  const byId = new Map();
  for (let i = 0; i <= lastIdx; i++) {
    const ev = events[i];
    let sp = byId.get(ev.spokeVeId);
    if (!sp) {
      sp = {
        veId: ev.spokeVeId,
        direction: ev.direction,
        source: ev.source,
        count: 0,
        firstT: ev.t,
        lastT: ev.t,
        // Coords are filled in by the caller (which knows about
        // client-geocoded fallbacks). We only carry the ids here.
        lat: ev.lat,
        lng: ev.lng,
        name: ev.name,
      };
      byId.set(ev.spokeVeId, sp);
    }
    sp.count += 1;
    sp.lastT = ev.t;
  }

  const visibleSpokes = Array.from(byId.values());
  return {
    visibleSpokes,
    currentTimestamp: lastIdx >= 0 ? events[lastIdx].t : startMs,
    cumulative: Object.fromEntries(visibleSpokes.map(s => [s.veId, s.count])),
  };
}
